import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

interface CtaSectionProps {
  title?: string;
  description?: string;
  primaryButtonText?: string;
  primaryButtonLink?: string;
  secondaryButtonText?: string;
  secondaryButtonLink?: string;
  darkMode: boolean;
  inView?: boolean;
  staticMode?: boolean;
}

export const CtaSection: React.FC<CtaSectionProps> = ({
  title = "Ready to start building solutions?",
  description = "Begin by creating a persona or exploring our pre-built workflows.",
  primaryButtonText = "Create a Persona",
  primaryButtonLink = "/personas/forge",
  secondaryButtonText = "Explore Workflows",
  secondaryButtonLink = "/workflows",
  darkMode,
  inView = true,
  staticMode = false
}) => {
  return (
    <div className="py-16 px-4">
      <motion.div 
        className="max-w-3xl mx-auto"
        initial={{ opacity: 0, y: 20 }}
        animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
        transition={{ duration: 0.8 }}
      >
        <motion.div 
          className={`p-8 rounded-lg ${
            darkMode 
              ? 'bg-gradient-to-r from-blue-900 to-indigo-900' 
              : 'bg-gradient-to-r from-blue-500 to-indigo-600'
          } text-white shadow-lg`}
          whileHover={{ y: -5, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" }}
        >
          <h2 className="text-2xl font-bold mb-4">{title}</h2>
          <p className="mb-6 opacity-90">
            {description}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              {staticMode ? (
                <button 
                  className={`px-6 py-3 rounded-lg ${
                    darkMode 
                      ? 'bg-white text-indigo-900 hover:bg-gray-100' 
                      : 'bg-white text-indigo-600 hover:bg-gray-100'
                  } font-medium transition-colors`}
                >
                  {primaryButtonText}
                </button>
              ) : (
                <Link 
                  to={primaryButtonLink} 
                  className={`px-6 py-3 rounded-lg ${
                    darkMode 
                      ? 'bg-white text-indigo-900 hover:bg-gray-100' 
                      : 'bg-white text-indigo-600 hover:bg-gray-100'
                  } font-medium transition-colors`}
                >
                  {primaryButtonText}
                </Link>
              )}
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              {staticMode ? (
                <button 
                  className={`px-6 py-3 rounded-lg ${
                    darkMode 
                      ? 'bg-indigo-800 text-white hover:bg-indigo-700 border border-white' 
                      : 'bg-indigo-700 text-white hover:bg-indigo-800'
                  } font-medium transition-colors`}
                >
                  {secondaryButtonText}
                </button>
              ) : (
                <Link 
                  to={secondaryButtonLink} 
                  className={`px-6 py-3 rounded-lg ${
                    darkMode 
                      ? 'bg-indigo-800 text-white hover:bg-indigo-700 border border-white' 
                      : 'bg-indigo-700 text-white hover:bg-indigo-800'
                  } font-medium transition-colors`}
                >
                  {secondaryButtonText}
                </Link>
              )}
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};
